import React, { useState } from 'react';
import { IconButton, MenuItem, Select } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SelectOption } from '../../types';

type Props = {
  gridRenderParams: GridRenderEditCellParams;
  initialChoiceValue: string;
  options: SelectOption[];
  setValueInsteadOfLabel?: boolean;
  convertNumber?: boolean;
  allowEmpty?: boolean;
  noOptionsLabel?: string;
};

const GridCellEditTextDropdown = (props: Props) => {
  const { t } = useTranslation();
  const {
    gridRenderParams,
    initialChoiceValue,
    options,
    setValueInsteadOfLabel = false,
    convertNumber = true,
    allowEmpty = false,
    noOptionsLabel = '',
  } = props;
  const [currentChoiceValue, setCurrentChoiceValue] = useState(initialChoiceValue);
  const { id: cellId, field } = gridRenderParams;

  const apiRef = useGridApiContext();

  const changeField = (dropdownValue: any) => {
    const newValue: string = dropdownValue as string;
    const chosenLabel = options.find((el) => el.value === newValue)?.label;
    setCurrentChoiceValue(newValue);

    if (setValueInsteadOfLabel) {
      apiRef.current.setEditCellValue({
        id: cellId,
        field,
        value: convertNumber ? parseInt(newValue, 10) : newValue,
      });
    } else {
      apiRef.current.setEditCellValue({ id: cellId, field, value: chosenLabel || '' });
    }
  };

  return (
    <Select
      value={allowEmpty ? currentChoiceValue || '' : currentChoiceValue}
      defaultValue={currentChoiceValue}
      onChange={(e) => {
        changeField(e.target.value);
      }}
      sx={{
        width: '100%',
        boxShadow: 'none',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
      }}
      endAdornment={
        allowEmpty && (
          <IconButton
            aria-label={t('actions.close')}
            size="small"
            onClick={() => changeField('')}
            tabIndex={0}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              marginRight: '1rem',
            }}
          >
            <CloseIcon />
          </IconButton>
        )
      }
    >
      {options.map((option) => (
        <MenuItem value={option.value} key={option.value}>
          {option.label}
        </MenuItem>
      ))}
      {allowEmpty && options?.length < 1 && (
        <MenuItem value="" key="" disabled>
          {noOptionsLabel ? t(noOptionsLabel) : t('common.noOptions')}
        </MenuItem>
      )}
    </Select>
  );
};

export default GridCellEditTextDropdown;
